import {
  calculateAdRemainingDays,
  DocumentBadgePlusIcon,
  getJobPositionStatusColor,
  getJobPositionStatusLabel,
  handleApiRequest,
  Loading,
  useFetchFile,
  UserTypeEnum,
} from "@cpj-ai/panel-npm-package";
import {
  AssignJobToJobSeekerMutation,
  AssignJobToJobSeekerMutationVariables,
  useAssignJobToJobSeekerMutation,
  useGetJobPositionsQuery,
  useGetJobSeekerByIdQuery,
} from "@graphql/generated";
import { Button, User } from "@heroui/react";
import { useCallback } from "react";

type SuggestionConfirmStepProps = {
  jobSeekerId: string | null;
  jobPositionId: string | null;
  onStepChange: (step: "confirmAssignment" | "jobPositionSelection") => void;
  onClose: () => void;
};

const SuggestionConfirmStep: React.FC<SuggestionConfirmStepProps> = ({
  jobSeekerId,
  jobPositionId,
  onStepChange,
  onClose,
}) => {
  const [assignJobToJobSeeker, { loading: assignJobToJobSeekerLoading }] = useAssignJobToJobSeekerMutation();

  const { data: jobSeeker, loading: jobSeekerLoading } = useGetJobSeekerByIdQuery({
    variables: { id: jobSeekerId ?? "" },
    skip: !jobSeekerId,
  });

  const { data: jobPosition, loading: jobPositionLoading } = useGetJobPositionsQuery({
    variables: {
      id: jobPositionId || "",
    },
    skip: !jobPositionId,
  });

  const handleSubmit = useCallback(async () => {
    await handleApiRequest<AssignJobToJobSeekerMutation, AssignJobToJobSeekerMutationVariables>({
      apiFunction: assignJobToJobSeeker,
      variables: {
        jobPositionId: jobPositionId,
        userId: jobSeekerId,
      },
      componentName: "SuggestionConfirmStep",
      actionName: "assignJobToJobSeeker",
      successMessage: "Job assigned successfully",
      fallbackErrorMessage: "Failed to assign job",
      onSuccess: () => {
        onClose();
      },
      extraErrorContext: {
        jobPositionId: "",
        userId: "",
      },
    });
  }, [assignJobToJobSeeker, jobSeekerId, jobPositionId, onClose]);

  const jobPositionData = jobPosition?.admin?.jobPositions?.edges?.[0]?.node;
  const jobSeekerData = jobSeeker?.admin?.jobSeekers?.edges?.[0]?.node;

  const validityDate = jobPositionData?.validityDate;
  const remainingValidityDays = validityDate
    ? calculateAdRemainingDays(validityDate) > 0
      ? `${calculateAdRemainingDays(validityDate)} day`
      : "Expired"
    : "Not set";

  const { data: avatarUrl, loading } = useFetchFile({
    urlPath: jobSeekerData?.profile?.avatar?.file,
    cache: "no-cache",
    userType: UserTypeEnum.ADMIN,
  });

  const { data: jobPositionImage, loading: jobPositionImageLoading } = useFetchFile({
    urlPath: jobPositionData?.organization?.logo?.file,
    cache: "no-cache",
    userType: UserTypeEnum.ADMIN,
  });

  const { fetchFile: fetchUserResumeBlob } = useFetchFile({
    urlPath: jobSeekerData?.resume?.file.file,
    downloadMode: true,
    userType: UserTypeEnum.ADMIN,
  });

  if (jobSeekerLoading || jobPositionLoading) {
    return <Loading />;
  }

  return (
    <div className="w-full">
      <p>Are you sure you want to refer the candidate to the job position?</p>

      <div>
        <p className="font-medium mt-5 mb-3">Candidate :</p>
        <div className="border border-zinc-300 py-5 px-8 bg-zinc-50 rounded-2xl">
          <User
            avatarProps={{
              src: jobSeekerData?.profile?.avatar && !loading ? avatarUrl : "",
              isDisabled: !jobSeekerData?.profile?.avatar,
              classNames: {
                img: "opacity-1",
              },
            }}
            name={`${jobSeekerData?.firstName || jobSeekerData?.lastName ? `${jobSeekerData?.firstName} ${jobSeekerData?.lastName}` : "-"}`}
            description={jobSeekerData?.email}
          />
          <div className="flex gap-8">
            <div className="flex items-center gap-2">
              <p className="text-xs">Score:</p>
              <p className="text-sm">{jobSeekerData?.profile?.score || "-"}</p>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-xs">Resume link:</p>
              {jobSeekerData?.resume?.file.file ? (
                <div onClick={fetchUserResumeBlob} className="text-tiny text-blue-500 cursor-pointer">
                  {jobSeekerData?.resume?.file.file?.split("/")?.at(-1)}
                </div>
              ) : (
                "-"
              )}
            </div>
            {/* <div className="flex items-center gap-2">
              <p className="text-xs">Current company :</p>
              <p className="text-sm ">{jobSeekerData?.workexperiences?.[0]?.organization || "-"}</p>
            </div> */}
          </div>
        </div>
      </div>
      <div>
        <p className="font-medium mt-5 mb-3">Job post :</p>
        <div className="border border-zinc-300 py-5 px-8 bg-zinc-50 rounded-2xl">
          <div className="flex gap-3 items-center border-b border-zinc-400 pb-4 mb-4">
            <div className="w-10 h-10 rounded-full bg-gray-200">
              {jobPositionImage && !jobPositionImageLoading && (
                <img src={jobPositionImage} alt="Job Position" className="w-full h-full rounded-full" />
              )}
            </div>
            <div className="flex w-full justify-between">
              <div className="flex gap-2 items-center">
                <p className="text-lg font-semibold">{jobPositionData?.title}</p>
                <p
                  className={`text-xs  rounded-lg text-white px-2 py-1 ${getJobPositionStatusColor(jobPositionData?.status)}`}
                >
                  {getJobPositionStatusLabel(jobPositionData?.status)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-sm me-1 text-zinc-500">Ad validity date :</p>
                <p className="text-sm me-2 text-zinc-500"> {jobPositionData?.validityDate}</p>
                <p className="text-sm py-1 px-2 bg-[#E3E4DB] rounded-lg">{remainingValidityDays}</p>
              </div>
            </div>
          </div>
          <div className="flex gap-8">
            <div className="flex items-center gap-2">
              <DocumentBadgePlusIcon className="w-6 h-6 stroke-black" />
              <p className="text-sm">Skills :</p>
              <p className="text-xs">{jobPositionData?.skills?.map((skill) => skill?.title).join(", ") || "-"}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-8">
        <Button
          color="default"
          variant="bordered"
          className="w-60 border-2 "
          isDisabled={assignJobToJobSeekerLoading}
          onPress={() => onStepChange("jobPositionSelection")}
        >
          Back
        </Button>
        <Button
          color="success"
          className="w-60"
          onPress={handleSubmit}
          isLoading={assignJobToJobSeekerLoading}
          isDisabled={assignJobToJobSeekerLoading}
        >
          Yes, assign
        </Button>
      </div>
    </div>
  );
};

export default SuggestionConfirmStep;
